import { useMutation, useQueryClient } from '@tanstack/react-query';

import { patientApi } from '../api/patient.adapter';
import type {
  ExportJobResult,
  ExportPatientsParams,
  PatientCreateResult,
  RegisterPatientInput,
  UpdatePatientInput,
} from '../types/patient.types';
import { patientQueryKeys } from './use-patients';

// New-patient registration (Root only — FEATURE_014). On success, invalidate the list so the new patient
// appears. (Status/closeReason transitions stay medical-web's, not admin's — PRD 02 §3.6 keeps 관리 상태
// read-only on this panel; 의료진 메모 was removed per RULE_MEMO_001.)
export function useRegisterPatient() {
  const qc = useQueryClient();
  return useMutation<PatientCreateResult, Error, RegisterPatientInput>({
    mutationFn: (input) => patientApi.registerPatient(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: patientQueryKeys.all });
    },
  });
}

// FEATURE_015 inline correction (PRD 02 v1.20 §3.6). Invalidating the whole `patients` root rather than
// patching the detail cache is deliberate: §3.6 조건 분기 requires the LIST row to refresh on save too, and
// several edited fields (이름 · 퇴원일) are list columns that also drive the current sort.
export function useUpdatePatient(id: number) {
  const qc = useQueryClient();
  return useMutation<void, Error, UpdatePatientInput>({
    mutationFn: (input) => patientApi.updatePatient(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: patientQueryKeys.all });
    },
  });
}

// [US-PM-07] Re-send the app-install SMS from the send-failure popup. No cache to invalidate — the SMS
// is a side effect, and nothing about the patient row changes when it goes out. The rejection is left
// to the caller so the popup can keep itself open and re-render the server's reason.
export function useResendInstallSms() {
  return useMutation<void, Error, number>({
    mutationFn: (patientId) => patientApi.resendInstallSms(patientId),
  });
}

// Root-only CSV export (FEATURE_062). The backend streams the CSV; this returns the downloaded Blob (not the
// query cache — it's consumed once to trigger the file download). useMutation gives isPending / error state.
export function useExportPatients() {
  return useMutation<Blob, Error, ExportPatientsParams>({
    mutationFn: (params) => patientApi.exportPatients(params),
  });
}

// Root-only ASYNC large-file export (FEATURE_062). Queues a background job; the file is delivered by email,
// not downloaded inline. Returns the 202 acknowledgement. Use this for exports too large for the sync stream.
export function useRequestPatientExportJob() {
  return useMutation<ExportJobResult, Error, ExportPatientsParams>({
    mutationFn: (params) => patientApi.requestExportJob(params),
  });
}
